<template>
    <b-modal
            v-model="visible"
            :id="modalId"
            :modal-class="[
                'get-connected-modal',
                {
                    'display-split': displaySplit,
                }
            ]"
            header-class="header-navigation p-0"
            hide-footer
            no-close-on-esc
    >
        <template #modal-header>
            <div class="navigation-button-wrapper">
                <b-button
                        v-if="canNavigateBack"
                        class="navigation-button back-button shadow-none bg-transparent"
                        size="lg"
                        variant="transparent"
                        @click="handleBack"
                >
                    <i class="far fa-arrow-left" />
                </b-button>
            </div>
            <div class="navigation-button-wrapper">
                <b-button
                        class="navigation-button close-button shadow-none bg-transparent"
                        size="lg"
                        variant="transparent"
                        @click="handleClose"
                >
                    <i class="far fa-times" />
                </b-button>
            </div>
        </template>

        <element-loader
                v-if="isLoading"
                loading
        />

        <component
                v-if="!isLoading"
                :is="currentStep"
                :resource="resource"
                :workspace="workspace"
                :landing-page="landingPage"
                :organization-member="organizationMember"
                :sender-user="senderUser"
                :service-providers="serviceProviders"
                :service-provider="serviceProvider"
                :selected-service-providers="selectedServiceProviders"
                :prefill-form="prefillForm"
                :meta="meta"
                :is-multiple="resource.isMultiSelect"
                :insurance="insurance"
                @connect="handleConnect"
                @sent="handleSent"
                @cancel="handleBack"
                @close="handleClose"
        >
            <template #service-provider-step-before-cards>
                <slot
                        name="service-provider-step-before-cards"
                        :resource="resource"
                        :handleClose="handleClose"
                />
            </template>
        </component>
    </b-modal>
</template>

<script>
    import './GetConnectedModal.scss';
    import SelectProviderStep from "../SelectProviderStep/SelectProviderStep.vue";
    import WriteMessageStep from "../WriteMessageStep/WriteMessageStep.vue";
    import WriteMessage2Step from "../WriteMessage2Step/WriteMessage2Step.vue";
    import MessageSentStep from "../MessageSentStep/MessageSentStep.vue";
    import MessageSent2Step from "../MessageSent2Step/MessageSent2Step.vue";
    import ServiceProviderAppResource from "@milestoneslabs/milestones-core-js/src/AppResources/ServiceProviderAppResource";
    import AppResourceCollection from "@milestoneslabs/milestones-js-engine/src/Data/AppResourceCollection";
    import ElementLoader from "../../../../components/ElementLoader/ElementLoader.vue";
    import ShowNotificationMixin from "../../../../mixins/ShowNotificationMixin";
    import isEmpty from 'lodash/isEmpty';
    import get from 'lodash/get';
    import ResourceAppResource from "@milestoneslabs/milestones-core-js/src/AppResources/ResourceAppResource";
    import WorkspaceAppResource from "@milestoneslabs/milestones-core-js/src/AppResources/WorkspaceAppResource";
    import LandingPageAppResource from "@milestoneslabs/milestones-core-js/src/AppResources/LandingPageAppResource";
    import UserAppResource from "@milestoneslabs/milestones-core-js/src/AppResources/UserAppResource";
    import OrganizationMemberAppResource
        from "@milestoneslabs/milestones-core-js/src/AppResources/OrganizationMemberAppResource";
    import HomeownersInsuranceAppResource
        from "@milestoneslabs/milestones-core-js/src/AppResources/HomeownersInsuranceAppResource";

    export const GET_CONNECTED_WITH_SERVICE_PROVIDER_SHOW = 'GET_CONNECTED_WITH_SERVICE_PROVIDER_SHOW';
    export const GET_CONNECTED_WITH_SERVICE_PROVIDER_MESSAGE_SENT = 'GET_CONNECTED_WITH_SERVICE_PROVIDER_MESSAGE_SENT';
    export const WORKSPACE_SOURCE = 'workspace-source';
    export const LANDING_PAGE_SOURCE = 'landing-page-source';

    export default {
        name: "GetConnectedModal",
        components: {
            SelectProviderStep,
            WriteMessageStep,
            WriteMessage2Step,
            MessageSentStep,
            MessageSent2Step,
            ElementLoader,
        },

        mixins: [
            ShowNotificationMixin,
        ],

        props: {
            source: {
                type: String,
                required: true,
            },

            skipSelect: {
                type: Boolean,
                default: false,
            },

            prefillForm: {
                type: Boolean,
                default: false,
            },
            insurance: {
                type: HomeownersInsuranceAppResource,
                default: () => {
                    return new HomeownersInsuranceAppResource();
                }
            }
        },

        data() {
            return {
                visible: false,
                currentStep: null,
                canNavigateBackFrom: [
                    WriteMessageStep,
                    WriteMessage2Step,
                    MessageSentStep,
                    MessageSent2Step,
                ],
                isLoading: false,
                resource: new ResourceAppResource({}),
                workspace: new WorkspaceAppResource({}),
                organizationMember: new OrganizationMemberAppResource({}),
                senderUser: new UserAppResource({}),
                landingPage: new LandingPageAppResource({}),
                serviceProviders: new AppResourceCollection('service-provider'),
                serviceProvider: null,
                selectedServiceProviders: [],
                meta: {},
                modalId: '',
            };
        },

        computed: {
            displaySplit() {
                return [WriteMessageStep, MessageSentStep].includes(this.currentStep);
            },

            canNavigateBack() {
                return this.canNavigateBackFrom.includes(this.currentStep);
            },
        },

        methods: {
            async init(data) {
                if (this.visible) {
                    return;
                }

                this.modalId = data.modalId;

                if (
                    isEmpty(data.resource) ||
                    !data.resource instanceof ResourceAppResource
                ) {
                    console.error('Invalid service providers resource.');
                    return;
                }

                if (this.prefillForm) {
                    if (
                        isEmpty(data.senderUser) ||
                        !data.senderUser instanceof UserAppResource ||
                        !data.senderUser.exists
                    ) {
                        console.error('Invalid user.');
                        return false;
                    }

                    this.senderUser = data.senderUser;
                    this.meta = data.meta || {};
                }

                if (this.validateSource(data)) {
                    if (this.skipSelect) {
                        if (
                            isEmpty(data.serviceProvider) ||
                            !data.serviceProvider instanceof ServiceProviderAppResource ||
                            !data.serviceProvider.exists
                        ) {
                            console.error('Invalid service provider.');
                            return;
                        }

                        this.serviceProvider = data.serviceProvider;
                        this.currentStep = WriteMessageStep;
                        this.canNavigateBackFrom = [MessageSentStep, MessageSent2Step];
                    }

                    this.resource = data.resource;
                    this.visible = true;

                    if (!this.skipSelect) {
                        this.currentStep = SelectProviderStep;
                        await this.fetchServiceProviders();
                    }
                }
            },

            validateSource(data) {
                if (this.source === WORKSPACE_SOURCE) {
                    if (
                        isEmpty(data.workspace) ||
                        !data.workspace instanceof WorkspaceAppResource ||
                        !data.workspace.exists
                    ) {
                        console.error('Invalid workspace.');
                        return false;
                    }

                    if (
                        isEmpty(data.organizationMember) ||
                        !data.organizationMember instanceof OrganizationMemberAppResource ||
                        !data.organizationMember.exists
                    ) {
                        console.error('Invalid organization membership.');
                        return false;
                    }

                    this.workspace = data.workspace;
                    this.organizationMember = data.organizationMember;

                    return true;
                }

                if (this.source === LANDING_PAGE_SOURCE) {
                    if (
                        isEmpty(data.landingPage) ||
                        !data.landingPage instanceof LandingPageAppResource ||
                        !data.landingPage.exists
                    ) {
                        console.error('Invalid landing page.');
                        return false;
                    }

                    this.landingPage = data.landingPage;

                    return true;
                }

                console.error('Invalid source.');
                return false;
            },

            async fetchServiceProviders() {
                this.isLoading = true;

                try {
                    const serviceProviderListUuid = get(
                        this.workspace,
                        'settings.generalSettings.serviceProviders.serviceProvidersListUuid',
                        null
                    );

                    if (isEmpty(serviceProviderListUuid)) {
                        throw Error('Service providers list is not set.');
                    }

                    const serviceProvider = new ServiceProviderAppResource({});
                    const response = await serviceProvider.searchWorkspaceServiceProviders({
                        serviceProviderListUuid: serviceProviderListUuid,
                        serviceProviderTypeUuid: this.resource.serviceProviderTypeUuid,
                        workspaceUuid: this.workspace.uuid,
                    });

                    serviceProvider.once('loaded', () => {
                        this.serviceProviders = response.appResourceCollection;
                        this.isLoading = false;

                        if (this.serviceProviders.length === 1) {
                            this.serviceProvider = this.serviceProviders.getFirst();
                            this.canNavigateBackFrom = [MessageSentStep, MessageSent2Step];
                            this.currentStep = WriteMessageStep;
                        }
                    });
                } catch (e) {
                    console.error(e);
                    this.showNotification({
                        type: 'error',
                        message: 'Something went wrong. Please, try again later.',
                    });
                }
            },

            handleBack() {
                if ([WriteMessageStep.name, WriteMessage2Step.name].includes(this.currentStep.name)) {
                    this.currentStep = SelectProviderStep;
                    return;
                }

                if (this.resource.isMultiSelect) {
                    this.currentStep = WriteMessage2Step;
                } else {
                    this.currentStep = WriteMessageStep;
                }

            },

            handleClose() {
                this.visible = false;

                if (!this.skipSelect) {
                    this.canNavigateBackFrom = [WriteMessageStep, MessageSentStep, MessageSent2Step];
                    this.currentStep = SelectProviderStep;
                    this.serviceProvider = null;
                }

                if (this.skipSelect) {
                    this.canNavigateBackFrom = [MessageSentStep, MessageSent2Step];
                    this.currentStep = WriteMessageStep;
                }
            },

            handleConnect(payload) {
                if (!Array.isArray(payload)) {
                    this.serviceProvider = payload;
                    this.currentStep = WriteMessageStep;
                }
                if (Array.isArray(payload) && this.resource.isMultiSelect) {
                    this.selectedServiceProviders = payload;
                    this.currentStep = WriteMessage2Step;
                }
            },

            handleSent() {
                if (this.resource.isMultiSelect) {
                    this.currentStep = MessageSent2Step;
                } else {
                    this.currentStep = MessageSentStep;
                }


                this.$uiBus.$emit(GET_CONNECTED_WITH_SERVICE_PROVIDER_MESSAGE_SENT);
            },
        },

        async beforeMount() {
            this.$uiBus.$on(GET_CONNECTED_WITH_SERVICE_PROVIDER_SHOW, this.init);
        },

        beforeDestroy() {
            this.$uiBus.$off(GET_CONNECTED_WITH_SERVICE_PROVIDER_SHOW, this.init);
        },
    };
</script>
